<template>
<div>
    <div class="flex items-center justify-end pt-6 pr-3">
        <div class="relative inline-block text-left pr-6" v-click-away="onClickAway">
            <div>
                <button @click="showSettings = !showSettings" type="button" class="flex items-center rounded-full text-black hover:text-gray-600" id="menu-button" aria-expanded="true" aria-haspopup="true">
                    <span class="sr-only">Open options</span>
                    <font-awesome-icon icon="fa-regular fa-gear" class="w-6 h-6" />
                </button>
            </div>

            <div v-show="showSettings" :class="showSettings ? 'animate__zoomIn' : ''" class="animate__animated w-96 absolute right-0 z-10 mt-2 origin-top-right rounded-md bg-white shadow-xl" role="menu" aria-orientation="vertical" aria-labelledby="menu-button" tabindex="-1">
                <div class="p-4" role="none">
                    <label class="relative inline-flex items-center cursor-pointer">
                        <input @change="mainStore.updateUserProgress(mainStore.exam_id)" v-model="mainStore.advanceQuestion" type="checkbox" value="" class="sr-only peer">
                        <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-black"></div>
                        <span class="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">Automatically advance to next question</span>
                    </label>

                </div>
            </div>
        </div>
    </div>

    <div class="grid place-items-center lg:h-[calc(85vh)] xs:h-[calc(100vh)]" v-touch:swipe.left="goToNext" v-touch:swipe.right="goBack">
        <div class="flex justify-between items-center">
            <button @click="goBack" type="button" class="inline-block h-full px-3 py-[20em] text-xs font-medium uppercase leading-normal text-primary transition duration-150 ease-in-out hover:border-primary-600 hover:bg-neutral-400 hover:bg-opacity-10 hover:text-primary-600 focus:border-primary-600 focus:text-primary-600 focus:outline-none focus:ring-0 active:border-primary-700 active:text-primary-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10" data-te-ripple-init data-te-ripple-color="light">
                <font-awesome-icon icon="fa-regular fa-arrow-left" class="w-6 h-6 mr-2" />
            </button>

            <div class="px-4 py-4 mx-auto max-w-6xl lg:py-20 xs:pb-60">

                <div class="max-w-6xl w-full mb-10 md:mx-auto sm:text-center md:mb-12">

                    <div>

                        <p class="inline-block px-3 py-px mb-4 text-xs font-semibold tracking-wider text-gray-400 uppercase rounded-full bg-teal-accent-400">
                            Question {{ mainStore.index + 1 }} of {{ mainStore.exams.flatMap(exam => exam.questions).length }}
                        </p>
                        <div class="w-[800px] h-auto mx-auto mb-6">
                            <vue-plyr v-show="mainStore.current.url && !mainStore.isLoadingUrl" ref="plyr" class="bg-gray-50">
                                <video ref="video" id="vid1" :src="mainStore.current.url" controls></video>
                            </vue-plyr>
                            <div v-if="mainStore.isLoadingUrl" class="flex items-center justify-center w-[800px] h-[600px] bg-gray-50 mb-6">
                                <div class="w-10 h-10 border-2 border-t-[4px] border-gray-200 rounded-full animate-spin"></div>
                            </div>
                        </div>
                        <h2 class="max-w-xl mb-6 font-sans text-3xl font-bold leading-none tracking-tight text-gray-900 md:mx-auto">
                            {{ mainStore.current.question }}
                        </h2>
                    </div>

                </div>
                <div class="max-w-lg space-y-3 sm:mx-auto lg:max-w-xl">

                    <div v-for="answer in mainStore.current.choices" @click="selectChoice(answer)" :class="mainStore.selectedChoice == answer ? 'bg-gray-50' : ''" class="flex items-center p-4 transition-colors cursor-pointer duration-200 border rounded shadow group hover:bg-gray-50 hover:border-gray-50">
                        <div class="mr-2">
                            <font-awesome-icon :icon="mainStore.selectedChoice == answer ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'" :class="mainStore.selectedChoice == answer ? 'text-black' : 'text-gray-200'" class="w-6 h-6 mr-2" />
                        </div>
                        <span class="text-gray-800 transition-colors duration-200 group-hover:text-black">{{ answer }}</span>
                    </div>
                    <!-- <p>Answer: {{ mainStore.current.answer }}</p> -->
                    
                    <!-- Resources Section -->
                    <div v-if="mainStore.current.resources && mainStore.current.resources.length > 0" class="mt-4">
                        <button 
                            @click="toggleResources()" 
                            class="flex items-center justify-between w-full text-left bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg p-3 transition-colors duration-200 shadow-sm"
                        >
                            <div class="flex items-center">
                                <font-awesome-icon icon="fa-solid fa-book" class="w-4 h-4 text-teal-600 mr-3" />
                                <span class="text-teal-800 font-semibold text-sm">Resources ({{ mainStore.current.resources.length }}):</span>
                            </div>
                            <font-awesome-icon 
                                :icon="showResources ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" 
                                class="w-4 h-4 text-teal-600 transition-transform duration-200"
                            />
                        </button>
                        
                        <div v-if="showResources" class="mt-2 bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                            <div class="space-y-2 p-3">
                                <div 
                                    v-for="(resources, type) in groupedResources" 
                                    :key="type"
                                    class="resource-type-section"
                                >
                                    <!-- Type Header -->
                                    <div class="flex items-center mb-2 pb-1 border-b" :class="resourceTypeConfig[type].borderClass">
                                        <div class="flex items-center space-x-2">
                                            <div class="w-4 h-4 rounded-full flex items-center justify-center" 
                                                :class="resourceTypeConfig[type].bgClass">
                                                <font-awesome-icon 
                                                    :icon="resourceTypeConfig[type].icon" 
                                                    :class="`${resourceTypeConfig[type].textClass} w-2 h-2`" 
                                                />
                                            </div>
                                            <h4 class="font-medium text-gray-900 text-xs">
                                                {{ resourceTypeConfig[type].label }}
                                            </h4>
                                            <span class="text-xs px-1.5 py-0.5 rounded-full text-xs" 
                                                :class="`${resourceTypeConfig[type].bgClass} ${resourceTypeConfig[type].textClass}`">
                                                {{ resources.length }}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <!-- Resources in this type -->
                                    <div class="space-y-1.5">
                                        <div 
                                            v-for="(resource, index) in resources" 
                                            :key="index"
                                            class="flex items-start space-x-2 p-2 rounded hover:bg-gray-50 transition-colors duration-150 border border-gray-100"
                                        >
                                            <div class="flex-shrink-0 mt-0.5">
                                                <div class="w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold text-white"
                                                    :class="resourceTypeConfig[type].badgeClass">
                                                    {{ index + 1 }}
                                                </div>
                                            </div>
                                            <div class="flex-1 min-w-0">
                                                <!-- Resource name and button on same line -->
                                                <div v-if="resource.name" class="flex items-center justify-between mb-1">
                                                    <h5 class="text-xs font-medium text-gray-900 truncate pr-2">{{ resource.name }}</h5>
                                                    <button 
                                                        v-if="resource.url"
                                                        @click="viewResource(resource)"
                                                        class="inline-flex items-center text-xs font-medium hover:underline transition-colors duration-150 px-2 py-1 rounded flex-shrink-0"
                                                        :class="resourceTypeConfig[type].linkClass"
                                                    >
                                                        <font-awesome-icon icon="fa-solid fa-external-link-alt" class="w-2.5 h-2.5 mr-1" />
                                                        {{ resource.linkText || 'View' }}
                                                    </button>
                                                </div>
                                                
                                                <!-- For resources without names, show button only -->
                                                <div v-else-if="resource.url" class="mb-1">
                                                    <button 
                                                        @click="viewResource(resource)"
                                                        class="inline-flex items-center text-xs font-medium hover:underline transition-colors duration-150 px-2 py-1 rounded"
                                                        :class="resourceTypeConfig[type].linkClass"
                                                    >
                                                        <font-awesome-icon icon="fa-solid fa-external-link-alt" class="w-2.5 h-2.5 mr-1" />
                                                        {{ resource.linkText || 'View' }}
                                                    </button>
                                                </div>
                                                
                                                <!-- Preview for images -->
                                                <div v-else-if="type === 'image' && resource.content" class="mt-1">
                                                    <img 
                                                        :src="resource.content" 
                                                        :alt="resource.name || 'Resource image'"
                                                        class="max-w-full h-auto rounded border border-gray-200 max-h-24 object-contain"
                                                    >
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="flex justify-end">

                        <div class="inline-flex rounded-md shadow-sm">
                            <button @click="flag()" :class="mainStore.flaggedQuestions.includes(mainStore.current.id) ? 'text-teal-400' : 'text-gray-700'" v-tooltip="{ content: mainStore.flaggedQuestions.includes(mainStore.current.id) ? 'Unflag Question' : 'Flag Question', html: true }" type="button" class="py-3 px-4 inline-flex hover:bg-gray-100 justify-center items-center gap-2 -ml-px first:rounded-l-lg first:ml-0 last:rounded-r-lg border font-medium bg-white align-middle sm:p-4">
                                <font-awesome-icon icon="fa-duotone fa-flag" class="w-5 h-5" />
                            </button>

                            <button @click="loadAndOpenNote()" :class="mainStore.notes.map(item => item.id).includes(mainStore.current.id) ? 'text-teal-400' : 'text-gray-700'" v-tooltip="{ content: mainStore.notes.map(item => item.id).includes(mainStore.current.id) ? 'Edit Note' : 'Add Note', html: true }" type="button" class="py-3 px-4 inline-flex hover:bg-gray-100 justify-center items-center gap-2 -ml-px first:rounded-l-lg first:ml-0 last:rounded-r-lg border font-medium bg-white align-middle sm:p-4">
                                <font-awesome-icon icon="fa-duotone fa-pen-to-square" class="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                </div>

            </div>

            <button @click="goToNext" type="button" class="-ml-0.5 inline-block h-full px-3 py-[20em] text-xs font-medium uppercase leading-normal text-primary transition duration-150 ease-in-out hover:border-primary-600 hover:bg-neutral-400 hover:bg-opacity-10 hover:text-primary-600 focus:border-primary-600 focus:text-primary-600 focus:outline-none focus:ring-0 active:border-primary-700 active:text-primary-700 dark:hover:bg-neutral-100 dark:hover:bg-opacity-10" data-te-ripple-init data-te-ripple-color="light">
                <font-awesome-icon icon="fa-regular fa-arrow-right" class="w-6 h-6 ml-2" />
            </button>

        </div>
        <div class="pb-32"></div>
    </div>

    <div class="bg-neutral-50 border-t border-gray-200 text-3xl h-20 text-white text-center fixed inset-x-0 bottom-0 pt-4 px-4">
        <div class="flex justify-between items-center">

            <div></div>
            <button @click="mainStore.showDisplayModal" class="text-[16px] lg:text-[16px] xs:text-[12px] relative inline-flex items-center justify-start py-2 pl-4 pr-4 overflow-hidden font-semibold text-black transition-all duration-150 ease-in-out rounded hover:pr-10 bg-gray-100 group">
                <span class="absolute bottom-0 left-0 w-full h-1 transition-all duration-150 ease-in-out bg-black group-hover:h-full"></span>
                <span class="absolute right-0 pr-4 duration-200 ease-out translate-x-12 group-hover:translate-x-0">
                    <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                    </svg>
                </span>

                <span class="relative w-full text-left transition-colors duration-200 ease-in-out group-hover:text-white">Submit Exam</span>
            </button>

        </div>

    </div>
    <div v-if="examShowPDFViewer" style="z-index: 9999;" class="transition duration-150 ease-in-out h-[100vh] bg-zinc-800 bg-opacity-[0.90] top-0 right-0 bottom-0 left-0 fixed">
        <div class="flex items-center justify-center">

            <div class="text-center text-white flex items-center justify-center">
                <div class="sm:p-12 h-[95vh]">
                    <div class="flex justify-end">
                        <button @click="examShowPDFViewer = false">
                            <font-awesome-icon icon="fa-solid fa-xmark" class="h-6 w-6 text-white" /></button>
                    </div>
                    <vue-pdf-app v-if="isPDF" class="w-[80vw] h-[85vh]" style="z-index: 9999;" :pdf="examResourceUrl"></vue-pdf-app>
                    <img v-else :src="examResourceUrl" class="max-h-[85vh] max-w-[80vw] object-contain" />
                </div>
            </div>
        </div>
    </div>
</div>
</template>

<script>
import useMainStore from '@/stores/main'
import {
    mapStores,
    mapActions
} from 'pinia';
import VuePdfApp from "vue3-pdf-app";
import "vue3-pdf-app/dist/icons/main.css";

import {
    directive
} from "vue3-click-away";

export default {
    name: "Quiz",
    components: {
        VuePdfApp,
    },
    directives: {
        ClickAway: directive,
    },
    data() {
        return {
            selectedAnswer: '',
            isProcessing: false,
            isLoading: true,
            showSettings: false,
            showResources: false,
            examShowPDFViewer: false,
            examResourceUrl: '',
            isPDF: false,
        }
    },
    computed: {
        ...mapStores(useMainStore),
        groupedResources() {
            if (!this.mainStore.current?.resources) return {};
            
            return this.mainStore.current.resources.reduce((groups, resource) => {
                let type = resource.type?.toLowerCase() || 'document';
                
                // Group all image types together
                if (type.includes('image')) {
                    type = 'image';
                }
                
                // Ensure we only use the 5 defined types
                const validTypes = ['link', 'document', 'pdf', 'text', 'image'];
                if (!validTypes.includes(type)) {
                    type = 'document'; // Default fallback
                }
                
                if (!groups[type]) {
                    groups[type] = [];
                }
                groups[type].push(resource);
                return groups;
            }, {});
        },
        resourceTypeConfig() {
            return {
                'link': { 
                    icon: 'fa-solid fa-link', 
                    bgClass: 'bg-teal-100',
                    textClass: 'text-teal-600',
                    borderClass: 'border-teal-200',
                    badgeClass: 'bg-teal-500',
                    linkClass: 'text-teal-600 hover:text-teal-800 bg-teal-50 hover:bg-teal-100',
                    label: 'Links' 
                },
                'document': { 
                    icon: 'fa-solid fa-file-lines', 
                    bgClass: 'bg-slate-100',
                    textClass: 'text-slate-600',
                    borderClass: 'border-slate-200',
                    badgeClass: 'bg-slate-500',
                    linkClass: 'text-slate-600 hover:text-slate-800 bg-slate-50 hover:bg-slate-100',
                    label: 'Documents' 
                },
                'pdf': { 
                    icon: 'fa-solid fa-file-pdf', 
                    bgClass: 'bg-rose-100',
                    textClass: 'text-rose-600',
                    borderClass: 'border-rose-200',
                    badgeClass: 'bg-rose-500',
                    linkClass: 'text-rose-600 hover:text-rose-800 bg-rose-50 hover:bg-rose-100',
                    label: 'PDFs' 
                },
                'text': { 
                    icon: 'fa-solid fa-file-lines', 
                    bgClass: 'bg-emerald-100',
                    textClass: 'text-emerald-600',
                    borderClass: 'border-emerald-200',
                    badgeClass: 'bg-emerald-500',
                    linkClass: 'text-emerald-600 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100',
                    label: 'Text Files' 
                },
                'image': { 
                    icon: 'fa-solid fa-image', 
                    bgClass: 'bg-indigo-100',
                    textClass: 'text-indigo-600',
                    borderClass: 'border-indigo-200',
                    badgeClass: 'bg-indigo-500',
                    linkClass: 'text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100',
                    label: 'Images' 
                }
            };
        }
    },
    watch: {
        'mainStore.current'() {
            // Reset resources dropdown when question changes
            this.showResources = false
        }
    },
    mounted() {
        this.$refs.video.addEventListener("loadedmetadata", (event) => {
            this.mainStore.isLoadingUrl = false
        });
    },
    created() {

    },
    methods: {
        ...mapActions(useMainStore, ['showDisplayModal', 'updateUserProgress', 'loadAndOpenNote', 'flag']),
        async viewResource(resource) {
            try {                
                this.isPDF = resource.type === 'pdf';

                if (this.isPDF) {
                    // Use the proxy endpoint to fetch the PDF
                    const response = await this.axios.get(`${import.meta.env.VITE_BASE_LINK}/proxy-pdf`, {
                        params: { url: resource.url },
                        responseType: 'blob'
                    });
                    
                    // Create a blob URL from the response
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    this.examResourceUrl = URL.createObjectURL(blob);
                } else if (resource.type == 'image') {
                    // For images, just use the URL directly
                    this.examResourceUrl = resource.url;
                } else if (resource.type == 'document') {
                    // For Word documents, open in new tab (browsers can't display them inline)
                    window.open(resource.url, '_blank');
                    return;
                } else {
                    // For other file types, open in new tab
                    window.open(resource.url, '_blank');
                    return;
                }
                this.examShowPDFViewer = true;
            } catch (error) {
                console.error('Error viewing file:', error);
                // Fallback to opening in new tab if there's an error
                window.open(resource.url, '_blank');
            }
        },
        onClickAway() {
            this.showSettings = false
        },
        toggleResources() {
            this.showResources = !this.showResources
        },
        selectChoice(choice) {

            this.mainStore.selectedChoice = choice

            // First, remove any existing answers for this question ID (handles edited questions)
            this.mainStore.answeredQuestions = this.mainStore.answeredQuestions.filter(q => q.id !== this.mainStore.current.id)
            
            // Add new answer
            this.mainStore.answeredQuestions.push({
                id: this.mainStore.current.id,
                question: this.mainStore.current.question,
                answer: choice
            })

            this.mainStore.current.userAnswer = choice

            this.updateUserProgress(this.$route.params.id)
            if (this.mainStore.advanceQuestion) {
                setTimeout(() => {
                    this.goToNext()
                }, 150);
            }
        },
        goBack() {
            if (this.mainStore.index == 0) return
            this.mainStore.index--
            this.mainStore.current = this.mainStore.questions[this.mainStore.index]
            
            const answeredIndex = this.mainStore.answeredQuestions.map(item => item.question).indexOf(this.mainStore.questions[this.mainStore.index].question)
            if (answeredIndex == -1) {
                this.mainStore.selectedChoice = ''
            } else {
                const answeredQuestion = this.mainStore.answeredQuestions[answeredIndex]
                const currentQuestion = this.mainStore.questions[this.mainStore.index]
                
                // Validate if the answer is still valid for current question
                if (this.mainStore.isAnswerValid(answeredQuestion, currentQuestion)) {
                    this.mainStore.selectedChoice = answeredQuestion.answer
                } else {
                    this.mainStore.selectedChoice = ''
                }
            }
            
            this.updateUserProgress(this.$route.params.id)
            window.navigator.vibrate(30);
        },
        goToNext() {
            if (this.mainStore.index == this.mainStore.questions.length - 1) return
            this.mainStore.index++
            this.mainStore.current = this.mainStore.questions[this.mainStore.index]

            const answeredIndex = this.mainStore.answeredQuestions.map(item => item.question).indexOf(this.mainStore.questions[this.mainStore.index].question)
            if (answeredIndex == -1) {
                this.mainStore.selectedChoice = ''
            } else {
                const answeredQuestion = this.mainStore.answeredQuestions[answeredIndex]
                const currentQuestion = this.mainStore.questions[this.mainStore.index]
                
                // Validate if the answer is still valid for current question
                if (this.mainStore.isAnswerValid(answeredQuestion, currentQuestion)) {
                    this.mainStore.selectedChoice = answeredQuestion.answer
                } else {
                    this.mainStore.selectedChoice = ''
                }
            }
            
            this.updateUserProgress(this.$route.params.id)
            window.navigator.vibrate(30);
        },

    }
}
</script>

<style>
.slide-fade-enter-active {
    transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
    transition: all 0.2s linear;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
    transform: translateY(30px);
    opacity: 0;
}

.slide-fade-leave-active {
    transition: all 0.7s linear;
}
</style>
